import "../../../shared/styles/list.css";

import {
  Button,
  Col,
  Input,
  notification,
  Space,
  Table,
  TablePaginationConfig,
  Typography,
} from "antd";
import { SorterResult } from "antd/lib/table/interface";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PlusCircleTwoTone, OrderedListOutlined } from "@ant-design/icons";

import {
  deleteServiceType,
  getServiceTypeList,
  getServiceTypePaginatedList,
  patchServiceTypeActiveForAllAdmins,
  reorderServiceTypes,
} from "../../../services/serviceTypeApi";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import ContainerLayout from "../../common/containerLayout";
import ReorderModal from "../../common/ReorderModal";
import ActivateServiceType from "../activateServiceType";
import DeactivateServiceType from "../deactivateServiceType";
import { columns, DataType } from "./columns";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
}

const { Text } = Typography;
const { Search } = Input;

const ServiceTypeList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [search, setSearch] = useState("");
  const [ignoreFetch, setIgnoreFetch] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [serviceTypeId, setServiceTypeId] = useState(0);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [reorderItems, setReorderItems] = useState<{ id: number; name: string }[]>([]);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [deactivateServiceTypeId, setDeactivateServiceTypeId] = useState(0);

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const fetchData = async () => {
    setLoading(true);
    const { data, total } = await getServiceTypePaginatedList({
      page: tableParams.pagination?.current || 1,
      take: tableParams.pagination?.pageSize || 10,
      search: search,
    });

    setData(data);
    setLoading(false);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        total,
      },
    });
    setIgnoreFetch(true);
  };

  useEffect(() => {
    if (!ignoreFetch) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams), search]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    sorter: SorterResult<DataType> | SorterResult<DataType>[],
  ) => {
    setTableParams({
      pagination,
      ...sorter,
    });
    setIgnoreFetch(false);
  };

  const handleDelete = async (serviceTypeId: any) => {
    await deleteServiceType(+serviceTypeId).then(() => {
      notification.success({
        message: `Tipo de Servicio #${serviceTypeId}`,
        description: "Se ha borrado exitosamente",
        placement: "bottom",
        key: "global",
      });
      fetchData();
    });
  };

  const onSearch = async (value: string) => {
    setIgnoreFetch(false);
    setSearch(value);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    });
  };

  const openModal = (serviceTypeId: number) => {
    setServiceTypeId(serviceTypeId);
    setIsModalOpen(true);
  };

  const openDeactivateModal = (serviceTypeId: number) => {
    setDeactivateServiceTypeId(serviceTypeId);
    setIsDeactivateModalOpen(true);
  };

  const activateServiceTypeForAll = async (
    serviceTypeId: number,
    isActiveForAllAdmins: boolean,
  ) => {
    await patchServiceTypeActiveForAllAdmins(
      serviceTypeId,
      !isActiveForAllAdmins,
    ).then(() => {
      notification.success({
        message: `El tipo de servicio #${serviceTypeId}`,
        description: "se asigno a todos los administradores exitosamente",
        placement: "bottom",
        key: "global",
      });
      setIgnoreFetch(false);
    });
  };

  const openReorderModal = async () => {
    const allItems = await getServiceTypeList();
    setReorderItems(
      (allItems || []).map((item: any) => ({
        id: item.id,
        name: item.name,
      })),
    );
    setIsReorderModalOpen(true);
  };

  return (
    <ContainerLayout title="Tipos de servicio" showNotification={false}>
      <Col span={24}>
        <Space>
          <Button
            onClick={() =>
              navigate(WebRoutes.CreateServiceTypeRoute, {
                replace: true,
              })
            }
            type="primary"
            icon={<PlusCircleTwoTone />}
          >
            Tipo de Servicio
          </Button>

          <Button icon={<OrderedListOutlined />} onClick={openReorderModal}>
            Reordenar
          </Button>

          <Text strong>Buscar</Text>
          <Search
            placeholder="Nombre del Tipo de Servicio"
            allowClear
            onSearch={onSearch}
            loading={loading}
            disabled={loading}
          />
        </Space>
      </Col>
      <Col span={24} style={{ width: "100px", height: "83vh" }}>
        <Table
          columns={columns(
            navigate,
            handleDelete,
            openModal,
            activateServiceTypeForAll,
            openDeactivateModal,
          )}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ y: "calc(100vh - 290px)" }}
        />
      </Col>
      <ActivateServiceType
        serviceTypeId={serviceTypeId}
        setIsModalOpen={setIsModalOpen}
        isModalOpen={isModalOpen}
      />

      <ReorderModal
        open={isReorderModalOpen}
        title="Reordenar tipos de servicio"
        items={reorderItems}
        onClose={() => setIsReorderModalOpen(false)}
        onSave={async (items) => {
          await reorderServiceTypes(items);
          fetchData();
        }}
      />
      <DeactivateServiceType
        serviceTypeId={deactivateServiceTypeId}
        setIsModalOpen={setIsDeactivateModalOpen}
        isModalOpen={isDeactivateModalOpen}
      />
    </ContainerLayout>
  );
};

export default ServiceTypeList;
